import React, { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TableView, TableHeader, TableBody, Column, Row, Cell, ProgressCircle, Heading, MenuTrigger, ActionButton, Menu, MenuItem, IllustratedMessage, Content } from "@react-spectrum/s2";
import type { SortDescriptor, LoadingState, Selection, DOMRefValue } from '@react-types/shared';
import type { ColumnSize } from '@react-types/table';
import type { CSSProperties, Key } from 'react';
import { More, NoSearchResultsIllustration, BrowserErrorIllustration } from '../icons/s2';
import { ColumnConfigurator } from './ColumnConfigurator';
import type { ColumnMeta } from './ColumnConfigurator';
import './DataTable.css';
/** Spectrum dimension tokens (`size-N`) map to px at `N * 0.08` (e.g. size-3000 = 240px).
 *  S2 sizing goes through the compile-time `style()` macro, which cannot accept the runtime
 *  token strings these props carry, so convert to a plain CSS length for `UNSAFE_style`. */ function toCssLength(token?: string): string | undefined {
    if (!token) return undefined;
    const match = /^size-(\d+)$/.exec(token);
    if (match) return `${Number(match[1]) * 0.08}px`;
    return token;
}
/** Numeric px value of a `size-N` token (or a plain px string), for comparing
 *  two height tokens. Relative lengths are left unchanged. */ function sizeTokenToPx(token?: string): number | undefined {
    const css = toCssLength(token);
    if (!css || !/^\d+(?:\.\d+)?px$/.test(css)) return undefined;
    const n = parseFloat(css);
    return Number.isNaN(n) ? undefined : n;
}
/** Floor applied to the table's own height while it renders a full-size
 *  IllustratedMessage (empty or error state). Callers that fit the table into
 *  a flex-constrained card pass a small `minHeight` (e.g. the Overview domains
 *  card's `size-1200` = 96px) so the populated table can shrink with its card;
 *  but with zero rows that floor collapses the empty-state illustration and
 *  clips its icon/heading.
 *
 *  Sizing: S2 renders the empty/error body at `containerHeight - 80px` (a 40px
 *  top + 40px bottom inset, see `buildRowGroup` in S2's TableView layout). The
 *  default M-size IllustratedMessage (96px icon + heading + up-to-2-line
 *  description) is ~190-200px tall, so the container needs ~280px+ for it to
 *  fit — the previous 192px floor left only ~112px and clipped the icon. 288px
 *  (`size-3600`) leaves ~206px of body, clearing the illustration, and only
 *  applies when there are no rows to scroll so it never re-introduces the
 *  populated-table overflow. */ const EMPTY_STATE_MIN_HEIGHT = 'size-3600';
/** Numeric px equivalent of {@link EMPTY_STATE_MIN_HEIGHT}; callers whose
 *  resolved `minHeight` is below this get floored to it while empty. */ const EMPTY_STATE_MIN_PX = 288;
/** Static vertical sizing for a `layout="fill"` table, expressed through the
 *  S2 `styles` macro rather than `UNSAFE_style`: the table takes a definite
 *  height from its bounded parent (`height: 'full'`) and never grows past it.
 *  The flex-fill wiring stays on the `.data-table--fill` stylesheet class (it
 *  also carries the per-page mobile reverts). `UNSAFE_style` is then reserved
 *  for genuinely build-time-unknown values only: the optional short-screen
 *  `minHeight` floor, and the `fixed`/`auto` band + caller token overrides. */ const FILL_SIZING_STYLES = " Za171 Fb171 Ka171";
const BASE_SIZING_STYLES = " Za171";
/** Visually-hidden header content: keeps the column's accessible name while rendering
 *  an empty header cell (S2 Column dropped v3's `hideHeader`). */ const SR_ONLY: CSSProperties = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0
};
export interface ColumnDefinition<T> {
    key: string;
    label: string;
    minWidth?: number;
    maxWidth?: number;
    width?: ColumnSize;
    /** Uncontrolled initial width for a resizable column. Unlike `width` (which is
   *  controlled and reverts on re-render), a resized `defaultWidth` column keeps
   *  the user's dragged width. Use this instead of `width` for `allowsResizing`. */ defaultWidth?: ColumnSize;
    allowsSorting?: boolean;
    allowsResizing?: boolean;
    isRowHeader?: boolean;
    hideHeader?: boolean;
    align?: 'start' | 'center' | 'end';
    showDivider?: boolean;
    render?: (item: T) => ReactNode;
    /** Custom header cell content (e.g. a select-all checkbox + label), in
   *  place of the plain label text. */ renderHeader?: () => ReactNode;
    /** When `configurableColumns` is enabled, pinned columns are always
   *  visible and cannot be reordered. This is purely a column-configurator
   *  concern. */ pinned?: boolean;
    /** Whether this column is visible on first load. Defaults to true. */ defaultVisible?: boolean;
    /** Exclude this column from the column configurator (e.g. a utility
   *  "Actions" column). It always renders and can't be toggled/reordered. */ excludeFromConfigurator?: boolean;
}
export interface ActionMenuItem {
    key: string;
    label: string;
    /** Shown but not selectable — e.g. "Reset to default" when there's nothing to reset. */ isDisabled?: boolean;
}
/**
 * Either a static menu (existing behavior for every current caller) or a
 * per-row function, for tables whose available actions differ by row (e.g.
 * a row sourced from a read-only registry vs. one the user owns outright).
 */ export type ActionMenuItemsProp<T> = ActionMenuItem[] | ((item: T) => ActionMenuItem[]);
export interface DataTableProps<T> {
    items: T[];
    columns: ColumnDefinition<T>[];
    loading?: boolean;
    loadingState?: LoadingState;
    ariaLabel: string;
    getRowKey: (item: T) => string;
    sortDescriptor?: SortDescriptor;
    onSortChange?: (descriptor: SortDescriptor) => void;
    onLoadMore?: () => void;
    onAction?: (actionKey: Key, item: T) => void;
    /** Called with the row's key when a row is clicked. Forwarded to TableView's `onAction`. */ onRowAction?: (rowKey: Key) => void;
    actionMenuItems?: ActionMenuItemsProp<T>;
    emptyStateTitle?: string;
    emptyStateDescription?: string;
    errorStateTitle?: string;
    errorStateDescription?: string;
    hasError?: boolean;
    density?: 'compact' | 'regular' | 'spacious';
    /** How cell content that exceeds the column width is handled. `'truncate'`
   *  (default) keeps single-line, fixed-height rows; `'wrap'` lets content
   *  wrap and rows grow to fit (needed for cells that render multiple
   *  chips/tags, e.g. a permissions list). */ overflowMode?: 'wrap' | 'truncate';
    /** How the table sizes itself vertically. This is the single knob that owns
   *  the scroll contract — callers declare intent, not a recipe of
   *  `height`/`maxHeight`/flex props.
   *
   *  - `'fill'` — fill the available height of a **bounded** parent and scroll
   *    rows internally. The component applies BOTH the flex-fill wiring and a
   *    definite height, so there is one correct behavior whether or not the
   *    table has a toolbar/header. The ONLY caller obligation is a parent that
   *    is itself a bounded `display:flex; flex-direction:column; min-height:0`
   *    box (e.g. a full-height panel). Replaces the old
   *    `fillContainer` + `height="100%"` + `maxHeight="100%"` incantation.
   *  - `'auto'` — size to content with no minimum floor. For short, complete
   *    (non-paginated) lists embedded in a card/section. Replaces the
   *    `minHeight="size-0"` override.
   *  - `'fixed'` (default) — a fixed min/max band (defaults 240–480px) or an
   *    explicit `height`. Suited to dialogs and auto-height cards. Honours
   *    `height` / `minHeight` / `maxHeight` overrides.
   *
   *  A `'fixed'` table whose rows exceed `maxHeight` without a definite
   *  `height` grows-then-clips (S2 needs a definite height to create a scroll
   *  viewport) — use `'fill'` (or an explicit `height`) for long/paginated
   *  content. */ layout?: 'fill' | 'auto' | 'fixed';
    /** `'fixed'` layout only: hard cap on the table's height. Defaults to
   *  `size-6000` (480px). Ignored by `'fill'` (which fills its parent). */ maxHeight?: string;
    /** Floor on the table's own height. In `'fixed'` layout defaults to
   *  `size-3000` (240px) so a table dropped into an unconstrained dialog/list
   *  never looks squished; `'auto'` defaults to no floor. In `'fill'` layout
   *  acts as a short-viewport floor so the table never collapses to nothing
   *  (the surrounding area scrolls to reach it). */ minHeight?: string;
    /** Show a column configurator that lets users show/hide and reorder columns. */ configurableColumns?: boolean;
    /** When provided, renders a header row with the label on the left. */ tableLabel?: string;
    /** Custom content rendered on the left of the header row (e.g. a filter
   *  control), aligned on the same row as the item count / column
   *  configurator. Takes precedence over `tableLabel` when both are set. */ toolbarStart?: ReactNode;
    /** Custom content rendered on the right of the header row (e.g. a primary
   *  "Add" button), alongside the column configurator. */ toolbarEnd?: ReactNode;
    /** Row selection mode. Defaults to 'none' (existing behavior for every current caller). */ selectionMode?: 'none' | 'single' | 'multiple';
    selectedKeys?: Selection;
    onSelectionChange?: (keys: Selection) => void;
    /** Render the TableView in Spectrum's quiet style (no outer border/background). */ isQuiet?: boolean;
    /** Selection affordance. Defaults to Spectrum's default ('checkbox'). */ selectionStyle?: 'checkbox' | 'highlight';
    /** `'fixed'` layout only: an explicit definite table height (e.g.
   *  `'size-4600'`) for a table that should always be that tall and scroll
   *  within it (a definite height is what gives S2 a real scroll viewport).
   *  Ignored by `'fill'` (which derives its height from the parent). */ height?: string;
    /** Override the built-in empty state (NotFound illustration + text) with a
   *  caller-supplied one (e.g. a folder illustration). */ emptyState?: () => React.ReactElement;
    /** Extra class(es) appended to the TableView's UNSAFE_className. */ tableClassName?: string;
    /** Draw a vertical divider after the first (row-header) column — a thin
   *  neutral line through the header + every body row-header cell, matching the
   *  Domains overview table. Opt-in because it only reads well when the first
   *  column is the row header. Implemented with an inset box-shadow (see
   *  `.data-table--first-col-divider` in DataTable.css) because S2's native
   *  `showDivider` is invisible on resizable columns. */ firstColumnDivider?: boolean;
    /** Row keys whose selection checkbox is disabled (read-only). A key that is
   * also in selectedKeys renders checked-but-disabled (can't be unchecked). */ disabledKeys?: Iterable<string>;
}
interface InternalColumn {
    key: string;
    id: string;
    label: string;
    minWidth?: number;
    maxWidth?: number;
    width?: ColumnSize;
    defaultWidth?: ColumnSize;
    allowsSorting?: boolean;
    allowsResizing?: boolean;
    isRowHeader?: boolean;
    hideHeader?: boolean;
    align?: 'start' | 'center' | 'end';
    showDivider?: boolean;
    renderHeader?: () => ReactNode;
}
export function DataTable<T>({ items, columns, loading = false, loadingState, ariaLabel, getRowKey, sortDescriptor, onSortChange, onLoadMore, onAction, onRowAction, actionMenuItems, emptyStateTitle, emptyStateDescription, errorStateTitle, errorStateDescription, hasError = false, density = 'spacious', overflowMode = 'truncate', layout = 'fixed', maxHeight, minHeight, configurableColumns = false, tableLabel, toolbarStart, toolbarEnd, selectionMode = 'none', selectedKeys, onSelectionChange, isQuiet = false, selectionStyle, height, emptyState, tableClassName, firstColumnDivider = false, disabledKeys }: DataTableProps<T>) {
    const { t } = useTranslation();
    const hasActions = !!onAction && (typeof actionMenuItems === 'function' ? true : !!actionMenuItems?.length);
    // -- Column configurator state ------------------------------------------
    const defaultOrder = useMemo(()=>columns.map((c)=>c.key), [
        columns
    ]);
    const defaultVisible = useMemo(()=>{
        const explicit = columns.filter((c)=>c.defaultVisible !== false).map((c)=>c.key);
        return new Set(explicit.length > 0 ? explicit : defaultOrder);
    }, [
        columns,
        defaultOrder
    ]);
    const [columnOrder, setColumnOrder] = useState<string[]>(defaultOrder);
    const [visibleKeys, setVisibleKeys] = useState<Set<string>>(defaultVisible);
    const columnMeta: ColumnMeta[] = useMemo(()=>columns// Header-less utility columns (e.g. an inline row-actions column with
        // no label) and columns explicitly opted out are always rendered and
        // must not appear as toggleable rows in the column configurator.
        .filter((c)=>!c.hideHeader && !c.excludeFromConfigurator).map((c)=>({
                key: c.key,
                label: c.label,
                pinned: c.pinned
            })), [
        columns
    ]);
    const colByKey = useMemo(()=>new Map(columns.map((c)=>[
                c.key,
                c
            ])), [
        columns
    ]);
    const activeColumns = useMemo(()=>{
        if (!configurableColumns) return columns;
        return columnOrder.filter((k)=>visibleKeys.has(k)).map((k)=>colByKey.get(k)!).filter(Boolean);
    }, [
        configurableColumns,
        columns,
        columnOrder,
        visibleKeys,
        colByKey
    ]);
    // -- Internal columns (add actions column if needed) --------------------
    const allColumns: InternalColumn[] = useMemo(()=>{
        const cols: InternalColumn[] = activeColumns.map((c)=>({
                key: c.key,
                id: c.key,
                label: c.label,
                minWidth: c.minWidth,
                maxWidth: c.maxWidth,
                width: c.width,
                defaultWidth: c.defaultWidth,
                allowsSorting: c.allowsSorting,
                allowsResizing: c.allowsResizing,
                isRowHeader: c.isRowHeader,
                hideHeader: c.hideHeader,
                align: c.align,
                showDivider: c.showDivider,
                renderHeader: c.renderHeader
            }));
        if (hasActions) {
            cols.push({
                key: '__actions',
                id: '__actions',
                label: t('components.dataTable.actions'),
                width: '1fr',
                allowsResizing: false,
                hideHeader: true
            });
        }
        return cols;
    }, [
        activeColumns,
        hasActions,
        t
    ]);
    const renderEmptyState = useCallback(()=>(<IllustratedMessage>
        {hasError ? <BrowserErrorIllustration/> : <NoSearchResultsIllustration/>}
        <Heading>{hasError ? (errorStateTitle ?? t('components.dataTable.errorTitle')) : (emptyStateTitle ?? t('components.dataTable.emptyTitle'))}</Heading>
        <Content>{hasError ? (errorStateDescription ?? t('components.dataTable.errorDescription')) : (emptyStateDescription ?? t('components.dataTable.emptyDescription'))}</Content>
      </IllustratedMessage>), [
        emptyStateTitle,
        emptyStateDescription,
        errorStateTitle,
        errorStateDescription,
        hasError,
        t
    ]);
    const getCellContent = useCallback((columnKey: Key, item: T): ReactNode =>{
        if (columnKey === '__actions') {
            const resolvedItems = typeof actionMenuItems === 'function' ? actionMenuItems(item) : actionMenuItems;
            if (!resolvedItems?.length) return null;
            return (<MenuTrigger>
            <ActionButton isQuiet aria-label={t('components.dataTable.actions')}>
              <More/>
            </ActionButton>
            <Menu onAction={(key)=>onAction!(key, item)} disabledKeys={resolvedItems.filter((mi)=>mi.isDisabled).map((mi)=>mi.key)}>
              {resolvedItems.map((mi)=>(<MenuItem key={mi.key} id={mi.key}>{mi.label}</MenuItem>))}
            </Menu>
          </MenuTrigger>);
        }
        const column = activeColumns.find((c)=>c.key === columnKey);
        if (!column) return '-';
        if (column.render) {
            return column.render(item);
        }
        const value = (item as Record<string, unknown>)[columnKey as string];
        if (value === null || value === undefined) {
            return '-';
        }
        // Cells truncate with an ellipsis (overflowMode="truncate"); the native
        // title reveals the full value on hover for long text.
        const text = String(value);
        return <span title={text}>{text}</span>;
    }, [
        activeColumns,
        onAction,
        actionMenuItems,
        t
    ]);
    const renderColumnHeader = useCallback((column: InternalColumn): ReactNode =>{
        // S2 Column has no `hideHeader`; keep the accessible name but hide it visually
        // so the header cell renders empty (e.g. the row-actions utility column).
        if (column.hideHeader) return <span style={SR_ONLY}>{column.label}</span>;
        if (column.renderHeader) return column.renderHeader();
        return <span title={column.label}>{column.label}</span>;
    }, []);
    // Dev-only guard: a `layout="fill"` table relies on a bounded flex-column
    // parent for its definite height. If that contract is broken the table
    // silently collapses to ~0px (no scrollbar, broken infinite scroll) — the
    // exact footgun the `layout` prop is meant to prevent. Warn loudly in the
    // dev server (never in tests/prod; jsdom has no layout) so misuse surfaces
    // at the call site. Hooks run before the `loading` early return.
    const tableRef = useRef<DOMRefValue<HTMLDivElement>>(null);
    useEffect(()=>{
        if (process.env.NODE_ENV !== 'development' || layout !== 'fill') return undefined;
        const node = tableRef.current?.UNSAFE_getDOMNode();
        if (!node) return undefined;
        const raf = requestAnimationFrame(()=>{
            if (node.getBoundingClientRect().height < 8) {
                // eslint-disable-next-line no-console
                console.warn(`[DataTable] A layout="fill" table (aria-label="${ariaLabel}") rendered ~0px tall. ` + 'Its parent is not a bounded `display:flex; flex-direction:column; min-height:0` ' + 'container, so the table collapsed. Host it in a <TablePanel> (or an equivalent ' + 'bounded flex column).');
            }
        });
        return ()=>cancelAnimationFrame(raf);
    }, [
        layout,
        ariaLabel,
        items.length,
        loading
    ]);
    if (loading) {
        return (<div className={" sd171 _Ca171 eb171 FDvO7ld171"}>
        <ProgressCircle aria-label={t('components.dataTable.loading')} isIndeterminate size="L"/>
      </div>);
    }
    const hasHeader = !!(tableLabel || toolbarStart || toolbarEnd || configurableColumns);
    // `layout` owns the vertical sizing/scroll contract so callers declare one
    // intent instead of coordinating `height` + `maxHeight` + a flex flag:
    //  - 'fill'  : flex-fill a bounded parent AND apply a definite height, for
    //    BOTH the header and headerless shapes, so a long/paginated table always
    //    scrolls internally. A definite height is what makes S2's internal
    //    `[role=grid]` a real scroll viewport — a bare `max-height` lets the grid
    //    grow to full content height and clip (no scrollbar, and cursor
    //    `onLoadMore` never fires). The only caller obligation is a bounded
    //    `display:flex; flex-direction:column; min-height:0` parent.
    //  - 'auto'  : size to content with no floor (short, complete lists).
    //  - 'fixed' : a 240-480px band (or an explicit `height`) for dialogs/cards.
    const isFill = layout === 'fill';
    const resolvedHeight = isFill ? '100%' : height;
    const resolvedMaxHeight = isFill ? '100%' : (maxHeight ?? 'size-6000');
    const resolvedMinHeight = isFill ? minHeight : (minHeight ?? (layout === 'auto' ? 'size-0' : 'size-3000'));
    // `.data-table--fill` (`flex:1 1 0; min-height:0`) on the TableView — plus
    // `.data-table-wrapper--fill` on the wrapper when the table has a header —
    // gives the S2 ResizableTableContainer a definite height inside a bounded
    // flex-column parent, for both the bare and header-wrapped shapes.
    const fillTable = isFill;
    // With zero rows the table renders a full IllustratedMessage; raise the
    // height floor so a small *definite* caller `minHeight` doesn't clip it (see
    // EMPTY_STATE_MIN_HEIGHT). Relative/undefined minHeights are left untouched,
    // and populated tables keep the resolved value.
    const minHeightPx = sizeTokenToPx(resolvedMinHeight);
    const effectiveMinHeight = items.length === 0 && minHeightPx !== undefined && minHeightPx < EMPTY_STATE_MIN_PX ? EMPTY_STATE_MIN_HEIGHT : resolvedMinHeight;
    const table = (<TableView aria-label={ariaLabel} isQuiet={isQuiet} selectionMode={selectionMode} selectionStyle={selectionStyle} selectedKeys={selectedKeys} onSelectionChange={onSelectionChange} disabledKeys={disabledKeys} disabledBehavior="selection" density={density} overflowMode={overflowMode} loadingState={loadingState} onLoadMore={onLoadMore} sortDescriptor={sortDescriptor} onSortChange={onSortChange} onAction={onRowAction} ref={tableRef} styles={isFill ? FILL_SIZING_STYLES : BASE_SIZING_STYLES} UNSAFE_style={isFill ? (effectiveMinHeight ? {
        minHeight: toCssLength(effectiveMinHeight)
    } : undefined) : {
        height: toCssLength(resolvedHeight),
        minHeight: toCssLength(effectiveMinHeight),
        maxHeight: toCssLength(resolvedMaxHeight)
    }} UNSAFE_className={`data-table${onRowAction ? ' data-table--clickable' : ''}${fillTable ? ' data-table--fill' : ''}${firstColumnDivider ? ' data-table--first-col-divider' : ''}${tableClassName ? ` ${tableClassName}` : ''}`}>
      <TableHeader columns={allColumns}>
        {(column)=>(<Column id={column.id} minWidth={column.minWidth} maxWidth={column.maxWidth} width={column.width} defaultWidth={column.defaultWidth} allowsSorting={column.allowsSorting} allowsResizing={column.allowsResizing} isRowHeader={column.isRowHeader} align={column.align} showDivider={column.showDivider}>
            {renderColumnHeader(column)}
          </Column>)}
      </TableHeader>
      <TableBody items={items.map((item)=>({
            ...item,
            id: getRowKey(item)
        }))} dependencies={[
        allColumns
    ]} renderEmptyState={emptyState ?? renderEmptyState}>
        {(item: T & {
        id: string;
    })=>(<Row id={item.id} columns={allColumns}>
            {(column)=>(<Cell>{getCellContent((column as InternalColumn).id, item)}</Cell>)}
          </Row>)}
      </TableBody>
    </TableView>);
    if (!hasHeader) return table;
    return (<div className={`data-table-wrapper${isFill ? ' data-table-wrapper--fill' : ''}`}>
      <div className="data-table-header-row">
        {toolbarStart ? (<div className="data-table-toolbar-start">{toolbarStart}</div>) : tableLabel ? (<span className="data-table-label">{tableLabel}</span>) : (<span/>)}
        {(toolbarEnd || configurableColumns) && (<div className="data-table-header-actions">
            {toolbarEnd}
            {configurableColumns && (<ColumnConfigurator allColumns={columnMeta} columnOrder={columnOrder} visibleKeys={visibleKeys} onOrderChange={setColumnOrder} onVisibilityChange={setVisibleKeys}/>)}
          </div>)}
      </div>
      <div className="data-table-body-wrap">
        {table}
      </div>
    </div>);
}
export type { SortDescriptor };

import "macro-e15377915f9f5c1d0ae2959bcf35f82675c24071515e20cbfee7137f951ad9ce.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-6e0aca53a35681127a4829dd779cf0f91a71d55c6da65245d53ca4802b7e4a51.css";